import { get, post, patch, del, put } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface User {
  id: string;
  username: string;
  name: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  roles: string[];
  customRoles?: Role[];
  department?: string;
  designation?: string;
  isActive: boolean;
  dashboardAccess?: boolean;
  createdAt: string;
  lastLoginAt?: string;
  employee?: {
    id: string;
    type: string;
    department?: { name: string };
    designation?: { name: string };
  };
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  isWebsiteOnly?: boolean;
  permissions?: Permission[];
  createdAt: string;
}

export interface Permission {
  id: string;
  code: string;
  name: string;
  description?: string;
  module: string;
  /** Optional presentation sub-group inside `module` (e.g. `payouts` in `billing`). */
  category?: string;
}

export interface RoleFilters {
  search?: string;
  status?: string;
  websiteOnly?: boolean;
}

export const userService = {
  getUsers: (page = 1, limit = 20, search?: string): Promise<ApiResponse<User[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.append('search', search);
    return get<User[]>(`/users?${params.toString()}`) as Promise<ApiResponse<User[]> & { meta: PaginationMeta }>;
  },

  getUser: (id: string): Promise<ApiResponse<User>> => {
    return get<User>(`/users/${id}`);
  },

  createUser: (data: any): Promise<ApiResponse<User>> => {
    return post<User>('/users', data);
  },

  updateUser: (id: string, data: any): Promise<ApiResponse<User>> => {
    return patch<User>(`/users/${id}`, data);
  },

  deleteUser: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/users/${id}`);
  },

  updateUserRoles: (id: string, roleIds: string[]): Promise<ApiResponse<User>> => {
    return put<User>(`/users/${id}/roles`, { roleIds });
  },

  getRoles: (
    page = 1,
    limit = 100,
    filters: RoleFilters = {},
  ): Promise<ApiResponse<Role[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.websiteOnly !== undefined) params.append('websiteOnly', String(filters.websiteOnly));
    return get<Role[]>(`/roles?${params.toString()}`) as Promise<ApiResponse<Role[]> & { meta: PaginationMeta }>;
  },

  getRole: (id: string): Promise<ApiResponse<Role>> => {
    return get<Role>(`/roles/${id}`);
  },

  createRole: (data: any): Promise<ApiResponse<Role>> => {
    return post<Role>('/roles', data);
  },

  updateRole: (id: string, data: any): Promise<ApiResponse<Role>> => {
    return patch<Role>(`/roles/${id}`, data);
  },

  deleteRole: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/roles/${id}`);
  },

  getRolePermissions: (id: string): Promise<ApiResponse<Permission[]>> => {
    return get<Permission[]>(`/roles/${id}/permissions`);
  },

  updateRolePermissions: (id: string, permissionIds: string[]): Promise<ApiResponse<Role>> => {
    return put<Role>(`/roles/${id}/permissions`, { permissionIds });
  },

  getRoleUsers: (id: string): Promise<ApiResponse<User[]>> => {
    return get<User[]>(`/roles/${id}/users`);
  },

  getPermissionsGrouped: (): Promise<ApiResponse<Record<string, Permission[]>>> => {
    return get<Record<string, Permission[]>>('/permissions/grouped');
  },

  adminResetPassword: (id: string, newPassword: string): Promise<ApiResponse<User>> => {
    return post<User>(`/users/${id}/reset-password`, { newPassword });
  },

  checkUsernameAvailability: (
    username: string,
    excludeId?: string,
  ): Promise<ApiResponse<{ available: boolean }>> => {
    const params = new URLSearchParams({ username });
    if (excludeId) params.append('excludeId', excludeId);
    return get<{ available: boolean }>(`/users/check-username?${params.toString()}`);
  },

  sendResetLink: (id: string): Promise<ApiResponse<void>> => {
    return post<void>(`/users/${id}/send-reset-link`, {});
  },

  generateResetLink: (id: string): Promise<ApiResponse<{ url: string; expiresAt: string }>> => {
    return post<{ url: string; expiresAt: string }>(`/users/${id}/reset-link`, {});
  },
};
